<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready" />
        <b-form @submit.prevent="onSubmit">
          <b-card header="Risk Mitigation Task" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <hm-alert />

            <h4>{{safeguard.title}}</h4>

            <h6 v-if="task.status == 'completed'" class="pt-2 pb-2">
              Status:
              <span class="text-success">
                <i class="icon-check"></i> Task completed
              </span>
            </h6>
            <h6 v-if="task.status == 'removed'" class="pt-2 pb-2">
              Status:
              <span class="text-danger">
                <i class="icon-close"></i> Task removed
              </span>
            </h6>

            <p class="prewrap">{{safeguard.desc}}</p>

            <div v-if="task.list && task.list.length > 0">
              Safeguard to be applied to:
              <div v-for="item in task.list" :key="item">
                <i class="fa fa-caret-right pr-2"></i>
                {{item}}
              </div>
            </div>

            <div v-if="safeguard.steps && safeguard.steps.length > 0" class="pt-3">
              After implementation, follow these steps to update security information:
              <p v-html="safeguard.steps"></p>
            </div>

            <b-form-group label="What is the estimated cost (first year) to implement this safeguard?" label-class="pt-3 font-weight-bold">
              <b-input-group class="mb-3">
                <b-input-group-prepend>
                  <b-input-group-text>$</b-input-group-text>
                </b-input-group-prepend>
                <vue-money
                  name="cost"
                  :precision="0"
                  v-model="task.cost"
                  v-validate="'required'"
                  :disabled="busy || !isEditable"
                  :class="{ 'p-2': true, 'is-invalid': errors.has('cost') && submitted }"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group label="Who is responsible for making sure this safeguard gets implemented?" label-class="font-weight-bold">
              <b-row>
                <b-col cols="6">
                  <b-form-select
                    name="assignee"
                    class="form-control"
                    :options="workforceNames"
                    v-model="task.assignee"
                    v-validate="'required'"
                    :disabled="busy || !isEditable"
                    :class="{ 'is-invalid': errors.has('assignee') && submitted }"
                  >
                    <template slot="first">
                      <option :value="''" disabled>-- Please select an option --</option>
                    </template>
                  </b-form-select>
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group label="When do you want to complete the implementation of this safeguard (due date)?" label-class="font-weight-bold">
              <b-row>
                <b-col cols="6">
                  <vue-datepicker
                    name="duedate"
                    placeholder="YYYY-MM-DD"
                    format="yyyy-MM-dd"
                    v-model="task.dueDate"
                    bootstrap-styling
                    :clear-button="isEditable"
                    clear-button-icon="fa fa-times-circle"
                    :disabledDates="disabledDates"
                    v-validate="'required'"
                    :disabled="busy || !isEditable"
                    :input-class="{ 'bg-white': true, 'is-invalid': errors.has('duedate') && submitted }"
                  />
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group label="Notes:" label-class="font-weight-bold">
              <b-form-textarea :rows="4" name="notes" class="form-control" placeholder="Optional notes for this task" v-model="task.note" :disabled="busy || !isEditable" />
            </b-form-group>

            <div slot="footer">
              <b-row>
                <b-col cols="6">
                  <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/task-list">Back</b-button>
                  <b-button type="button" variant="danger" class="px-4 ml-2" :disabled="busy" v-if="id && isEditable" @click="dlgRemove = true">Remove</b-button>
                </b-col>
                <b-col cols="6" class="text-right">
                  <b-button type="button" variant="primary" class="px-4" :disabled="busy" v-if="id && task.status && task.status != 'open'" @click="onSubmitOpen">Re-open</b-button>
                  <b-button type="button" variant="success" class="px-4" :disabled="busy" v-if="id && isEditable" @click="onShowComplete">Task Complete</b-button>
                  <b-button type="submit" variant="primary" class="px-4 ml-2" :disabled="busy" v-if="id && isEditable">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>

    <!-- Remove Modal Dialog -->
    <b-form @submit.prevent="onSubmitRemove">
      <b-modal id="dlgRemove" class="modal-primary" hide-header-close v-model="dlgRemove" @shown="onShowRemove">
        <div class="font-weight-bold" slot="modal-title">
          <i class="fa fas fa-trash pr-2"></i>Remove Task Confirmation
        </div>
        <hm-alert />
        <p>
          <b-badge class="text-uppercase" variant="warning" v-if="requiredSafeguard">HIPAA Required Safeguard</b-badge>
        </p>
        <p>Are you sure you want to remove this item?</p>
        <b-input-group class="mb-3">
          <b-form-textarea
            no-resize
            :rows="3"
            name="dlgRemoveReason"
            class="form-control"
            placeholder="Why is this task being removed?"
            v-model="dlgRemoveReason"
            :disabled="loading"
            v-validate="'required'"
            :class="{ 'is-invalid': errors.has('dlgRemoveReason') && dlgRemoveSubmitted }"
          />
          <b-form-invalid-feedback id="dlgRemoveReasonFeedback">Please provide a reason for removing this task</b-form-invalid-feedback>
        </b-input-group>
        <div slot="modal-footer">
          <b-button variant="default" :disabled="loading" @click="dlgRemove = false">Cancel</b-button>
          <b-button type="submit" variant="primary" class="ml-2" :disabled="loading" @click="onSubmitRemove">
            <i class="fa fa-refresh fa-spin" v-if="loading"></i> Remove
          </b-button>
        </div>
      </b-modal>
    </b-form>

    <!-- Complete Modal Dialog -->
    <b-modal id="dlgComplete" class="modal-primary" hide-header-close v-model="dlgComplete">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-check-square pr-2"></i>Complete Confirmation
      </div>
      <p>Are you sure you want to mark this task complete?</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgComplete = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgComplete = false; onSubmitComplete();">Yes</b-button>
      </div>
    </b-modal>

    <!-- Complete Confirmation Modal Dialog -->
    <b-modal id="dlgCompleteConfirm" class="modal-primary" hide-header-close v-model="dlgCompleteConfirm">
      <div class="font-weight-bold" slot="modal-title">
        <i class="icon-paper-plane pr-2"></i>Task Complete Successful
      </div>
      <p>This task has been successfully completed. Great job!</p>
      <div slot="modal-footer">
        <b-button variant="primary" @click="onCloseComplete">OK</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { riskService, workforceService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'TaskEdit',
  props: ['id'],
  data() {
    return {
      rm: {
        tasks: []
      },
      task: {
        status: 'open',
        cost: 0,
        effectiveness: ''
      },
      workforceNames: [],
      disabledDates: {
        to: new Date(Date.now() - 8640000)
      },
      dlgRemove: false,
      dlgRemoveReason: '',
      dlgRemoveSubmitted: false,
      dlgComplete: false,
      dlgCompleteConfirm: false,
      ready: false,
      busy: false,
      submitted: false
    }
  },
  computed: {
    ...mapState('maps', ['safeguardsMap']),
    ...mapGetters({
      loading: 'isLoading',
    }),
    isEditable() {
      return (!this.task.hasOwnProperty('status') || this.task.status == 'open');
    },
    safeguard() {
      return Enumerable.from(JSON.parse(JSON.stringify(this.safeguardsMap)))
        .where(x => { return x.id == this.id; })
        .firstOrDefault(null, {});
    },
    requiredSafeguard() {
      let safeguard = this.safeguard;
      return safeguard ? safeguard.type === 'required' : false;
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertSuccess: 'alert/success',
      alertError: 'alert/error',
      alertException: 'alert/exception'
    }),
    onShowRemove() {
      this.alertClear();
      this.dlgRemoveReason = '';
      this.dlgRemoveSubmitted = false;
    },
    onSubmitRemove() {
      this.alertClear();
      this.dlgRemoveSubmitted = true;

      this.$validator.validate()
        .then(valid => {
          if (this.dlgRemoveReason && this.dlgRemoveReason.length > 0) {
            // mark task as removed
            this.task.status = 'removed';
            this.task.completedDate = new Date();
            this.task.completedReason = this.dlgRemoveReason;

            return riskService.updateRiskManagement(this.rm)
              .then(data => {
                this.dlgRemove = false;
                this.$router.push('/task-list');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error removing risk mitigation task.' });
              });
          }
        });
    },
    onShowComplete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            // show confirmation dialog
            this.dlgComplete = true;
          } else {
            this.alertError('Please complete risk mitigation information before marking complete.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSubmitComplete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            // mark task as completed
            this.task.status = 'completed';
            this.task.completedDate = new Date();
            this.task.completedReason = null;

            return riskService.updateRiskManagement(this.rm)
              .then(data => {
                this.$confetti.start({ canvasId: null, particles: [{ type: 'rect' }] });

                // show task completion dialog
                this.dlgCompleteConfirm = true;
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving risk mitigation task.' });
              });
          } else {
            this.alertError('Please complete risk mitigation information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onCloseComplete() {
      this.$confetti.stop();
      this.dlgCompleteConfirm = false;
      this.$router.push('/task-list');
    },
    onSubmitOpen() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      // mark task as open
      this.task.status = 'open';

      return riskService.updateRiskManagement(this.rm)
        .then(data => {
          this.alertSuccess('Risk mitigation task re-opened successfully!');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error updating risk mitigation task.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return riskService.updateRiskManagement(this.rm)
              .then(data => {
                this.$router.push('/task-list');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving risk mitigation task.' });
              });
          } else {
            this.alertError('Please complete risk mitigation information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchSafeguardsMap'),
        workforceService.getWorkforce(),
        riskService.getActiveRiskManagement()
      ]).then((values) => {
        let workforce = (values[1] && values[1].length > 0) ? values[1] : [];
        let rm = values[2];

        // make sure that we have a valid id for the active risk management plan
        if (!this.id || !rm.tasks[this.id]) {
          return this.alertException({ err, message: 'Invalid task id.' });
        }

        // build list of workforce names plus last assignee value
        let names = Enumerable.from(workforce).where(x => { return x.active; }).select(x => { return x.name; }).toArray();
        if (names.indexOf(rm.tasks[this.id].assignee) < 0) {
          names.push(rm.tasks[this.id].assignee);
        }

        this.rm = rm;
        this.workforceNames = names;
        this.task = this.rm.tasks[this.id];
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>


<style>
.prewrap {
  white-space: pre-wrap;
}
</style>